import Ten99RouteConstants from 'Agent/1099s/Ten99RouteConstants';
import CommissionsRouteConstants from 'Agent/Commissions/CommissionsRouteConstants';
import {push} from 'connected-react-router';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {fromResult, Loaded, Loading, ServerError} from 'Core/models/Async';
import {ValidPassword} from 'Core/models/Password';
import {isRight} from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, call, put, select, takeLatest, takeEvery} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {refreshToken} from 'Shared/utilities/Auth';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import userApi from 'Users/api/userApi';
import {
  changePassword,
  changeUpdatePasswordError,
  setSelectedAgency,
  setUserClaims,
  updatePassword,
  UserActionTypes,
} from 'Users/ducks/userProfile';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {createSnackbar} from 'Core/ducks/snackbar';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {getIsNewYorkFromAgency} from 'Shared/utilities/isNewYorkUtility';
import {eq} from 'Shared/utilities/equals';
import {findFirst, head} from 'fp-ts/lib/Array';

const isCommissionsRoute = (pathname: string) => pathname.includes(CommissionsRouteConstants.COMMISSIONS);
const is1099sRoute = (pathname: string) => pathname.includes(Ten99RouteConstants.TEN99s);
const isCurrentlyAtPageOnlyAccessibleToActiveAgencies = (pathname: string) =>
  isCommissionsRoute(pathname) || is1099sRoute(pathname);

function* handleTaxIdSwitchSaga() {
  yield takeLatest(UserActionTypes.SET_SELECTED_AGENCY, function*(action: ReturnType<typeof setSelectedAgency>) {
    const {selectedAgency} = action.payload;
    const location: Location = yield select((state: ReducerState) => state.router.location);
    const hasAllAgentNumbersTerminated = pipe(
      selectedAgency,
      O.map(agency => agency.overallAgentNumberStatus.id === AgentNumberStatusId.Terminated),
      O.getOrElse(() => false)
    );

    if (!isCurrentlyAtPageOnlyAccessibleToActiveAgencies(location.pathname) && hasAllAgentNumbersTerminated) {
      yield put(push(CoreRouteConstants.DASHBOARD));
    }
  });
}

function* updatePasswordByUserSaga() {
  yield takeLatest(UserActionTypes.UPDATE_PASSWORD, function*(action: ReturnType<typeof updatePassword>) {
    yield put(changePassword(new Loading()));

    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );

    yield put(changePassword(new Loading()));

    const identifier = !O.isNone(selectedAgency) ? selectedAgency.value.encryptedIdentifier : '';

    const result = yield call(
      userApi.updatePasswordByUser,
      action.payload.userId,
      action.payload.updatePasswordRequest,
      identifier
    );

    if (isRight(result)) {
      yield put(changeUpdatePasswordError());
      yield put(createSnackbar('Password updated successfully.'));

      if (action.payload.onReset) {
        action.payload.onReset();

        yield call(refreshToken);

        const newClaims = pipe(getUserClaims());

        if (O.isSome(newClaims)) {
          yield put(setUserClaims(fromResult(newClaims.value)));
        }
      }
    } else {
      yield put(
        changeUpdatePasswordError(
          result.left instanceof ServerError
            ? result.left.errorDetails
            : 'An unknow error has occurred. Please try again later.'
        )
      );
    }
    yield put(changePassword(new Loaded(ValidPassword.create('', ''))));
  });
}

export function* refreshUserTokenSaga() {
  yield call(refreshToken);

  const newClaims = pipe(getUserClaims());
  if (O.isSome(newClaims)) {
    const agentClaims = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
      O.chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.AgentSite]: agentClaims => {
            const impersonatedAgentClaims = O.fromNullable(agentClaims.impersonatedAgentClaim);
            return O.isSome(impersonatedAgentClaims) ? impersonatedAgentClaims : O.some(agentClaims);
          },
          default: () => O.none,
        })
      )
    );
    const enrollTrackAgentAgencyAssociations = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.EnrollTrack)),
      O.chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.EnrollTrack]: enrollTrackClaims =>
            enrollTrackClaims.impersonatedEnrollTrackClaim
              ? O.fromNullable(enrollTrackClaims.impersonatedEnrollTrackClaim.agencyAssociations)
              : O.none,
          default: () => O.none,
        })
      )
    );

    if (O.isSome(agentClaims)) {
      const defaultAgency = pipe(agentClaims.value.agencyAssociations, head);

      const isNewYork = getIsNewYorkFromAgency(defaultAgency);
      yield put(setSelectedAgency(defaultAgency, isNewYork));
    }

    if (O.isSome(enrollTrackAgentAgencyAssociations)) {
      const defaultAgency = pipe(enrollTrackAgentAgencyAssociations.value, head);

      const isNewYork = getIsNewYorkFromAgency(defaultAgency);

      yield put(setSelectedAgency(defaultAgency, isNewYork));
    }
  }
}

export function* refreshUserToken() {
  yield takeEvery(UserActionTypes.REFRESH_USER_TOKEN, refreshUserTokenSaga);
}
export default function*() {
  yield all([handleTaxIdSwitchSaga(), updatePasswordByUserSaga(), refreshUserToken()]);
}
